'use client'

import { sva, type RecipeVariantProps } from '@/styled-system/css'
import { memo, type CSSProperties } from 'react'
import useGameSettings from '../shared/settings/useGameSettings'
import KenoGameGem from './gem'

const tileRecipe = sva({
  slots: ['root', 'cover', 'number', 'gem'],
  base: {
    root: {
      position: 'relative',
      color: 'text.primary',
      fontSize: '1.5em',
      fontWeight: 'bold',
      textShadow: '0 3px 2px rgba(0, 0, 0, 0.1)',
      borderRadius: '0.5em',
      transitionProperty: 'background-color, box-shadow, opacity',
      transitionDuration: 'var(--duration)',
      cursor: 'pointer',
      _after: {
        content: '""',
        display: 'block',
        pb: '100%',
      },
      _disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        pointerEvents: 'none',
      },
    },
    cover: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '0.5em',
      transitionProperty: 'background-color, box-shadow, transform',
      transitionDuration: 'var(--duration)',
      animationDuration: 'var(--duration)',
    },
    number: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
    },
    gem: {
      display: 'flex',
      position: 'absolute',
      top: '50%',
      left: '50%',
      w: '66.6667%',
      h: '66.6667%',
      transform: 'translate(-50%, -50%)',
      animationName: 'reveal',
      animationDuration: 'var(--duration)',
      animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
      animationFillMode: 'forwards',
    },
  },
  variants: {
    kind: {
      hidden: {
        cover: {
          bg: 'neutral.300',
          boxShadow: '0 0.3em token(colors.neutral.200)',
          animationName: 'scale-in',
          animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
          _hover: {
            bg: 'neutral.400',
            boxShadow: '0 0.3em token(colors.neutral.300)',
          },
          _active: {
            bg: 'neutral.400',
            boxShadow: '0 0.15em token(colors.neutral.300)',
            transform: 'translateY(0.15em)',
          },
        },
      },
      selected: {
        root: {
          _disabled: {
            opacity: 1,
          },
        },
        cover: {
          bg: 'primary',
          boxShadow: '0 0.3em token(colors.pink.950)',
          animationName: 'scale-in',
          animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
          _hover: {
            bg: 'primary',
            boxShadow: '0 0.3em token(colors.pink.950)',
          },
          _active: {
            bg: 'primary',
            boxShadow: '0 0.15em token(colors.pink.950)',
            transform: 'translateY(0.15em)',
          },
        },
      },
      revealed: {
        root: {
          bg: 'neutral.50',
          boxShadow: 'inset 0 0.3em #000',
          color: 'primary',
        },
        cover: {
          bg: 'neutral.300',
          boxShadow: '0 0.3em token(colors.neutral.200)',
          animationName: 'scale-out',
          animationFillMode: 'forwards',
        },
      },
      match: {
        root: {
          bg: 'neutral.50',
          boxShadow:
            '0 0.3em token(colors.pink.950), inset 0 0 0 0.1em token(colors.primary)',
          color: 'pink.950',
          textShadow: '0 1px #FFA3E5',
          _disabled: {
            opacity: 1,
          },
        },
        cover: {
          bg: 'primary',
          boxShadow: '0 0.3em token(colors.pink.950)',
          animationName: 'scale-out',
          animationFillMode: 'forwards',
        },
      },
    },
  },
})

export type KenoTileProps = RecipeVariantProps<typeof tileRecipe> & {
  number: number
  disabled?: boolean
  onClick?: () => void
}

const KenoTile = memo(function KenoGameTile(props: KenoTileProps) {
  const [variantProps, tileProps] = tileRecipe.splitVariantProps(props)
  const { number, ...rootProps } = tileProps
  const styles = tileRecipe(variantProps)
  const animationsEnabled = useGameSettings((state) => state.animationsEnabled)

  return (
    <button
      className={styles.root}
      style={
        {
          '--duration': animationsEnabled ? '0.3s' : '0s',
        } as CSSProperties
      }
      {...rootProps}
    >
      {variantProps.kind === 'match' ? (
        <div className={styles.gem}>
          <KenoGameGem />
        </div>
      ) : null}

      <span className={styles.number}>{number}</span>

      <div className={styles.cover}>
        <span className={styles.number}>{number}</span>
      </div>
    </button>
  )
})

export default KenoTile
